import axios from 'axios';

export class ElevationService {
    constructor() {
        // Use backend API server for production-ready elevation data
        this.useBackend = import.meta.env.VITE_USE_BACKEND === 'true';
        this.backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001';
        
        if (this.useBackend) {
            console.log('✓ Using backend API server for elevation data (scalable)');
        } else {
            console.log('✓ Using Open-Elevation API for elevation data (free)');
        }
        
        // Fallback to Open-Elevation API (free, no key required)
        this.openElevationUrl = 'https://api.open-elevation.com/api/v1/lookup';
    }

    /**
     * Get elevation data for a path
     * @param {Array} coordinates - Array of {lat, lng} objects
     * @param {number} samples - Number of samples along the path
     */
    async getElevationAlongPath(coordinates, samples = 50) {
        try {
            return await this.getRealElevationData(coordinates, samples);
        } catch (error) {
            console.error('❌ Error fetching elevation data:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                useBackend: this.useBackend,
                backendUrl: this.backendUrl
            });
            console.log('⚠️ Falling back to mock elevation data');
            return this.generateMockElevationData(coordinates, samples);
        }
    }

    /**
     * Get real elevation data from backend or Open-Elevation API
     */
    async getRealElevationData(coordinates, samples) {
        // Sample the coordinates to get the desired number of points
        const sampledCoords = this.sampleCoordinates(coordinates, samples);

        const result = this.useBackend
            ? await this.getElevationFromBackend(sampledCoords, coordinates)
            : await this.getElevationFromOpenElevation(sampledCoords, coordinates);

        // Grade warnings get each sample's real distance along the route.
        // `distance` spaces the samples evenly by count, but they're every Nth
        // route point, and route points bunch up on curves: on I-70 a 0.42 mi
        // gap was labelled 0.11 mi and read as 20% (drive 2026-11-02). Route
        // scoring and the route card still use `distance` and stats as before.
        const routeMi = this.routeMilesAt(coordinates, sampledCoords);
        if (routeMi.length === result.points.length) {
            result.points.forEach((p, i) => { p.routeMi = routeMi[i]; });
            result.stats.gradeStretches = this.findGradeStretches(result.points);
        }
        return result;
    }

    /** Distance along the route (miles) to each sampled point. */
    routeMilesAt(coordinates, sampledCoords) {
        const at = new Map();
        let total = 0;
        coordinates.forEach((c, i) => {
            if (i > 0) total += this.haversineDistance(coordinates[i - 1].lat, coordinates[i - 1].lng, c.lat, c.lng);
            if (!at.has(c)) at.set(c, total);
        });
        return sampledCoords.map(c => at.get(c)).filter(d => d !== undefined);
    }

    /**
     * Steep stretches for grade warnings, one warning each. Grade is measured
     * over about a quarter mile of real distance (single sample pairs are
     * noisy) and isn't capped. Steep points closer than a mile apart are one
     * stretch unless the road switches between climbing and descending, so a
     * downhill always gets its own warning. Each stretch starts where it first
     * gets steep and carries its steepest grade.
     */
    findGradeStretches(points, { windowMi = 0.25, minGradePct = 5, joinMi = 1 } = {}) {
        const windows = [];
        for (let i = 1; i < points.length; i++) {
            let j = i - 1;
            while (j > 0 && points[i].routeMi - points[j].routeMi < windowMi) j--;
            const miles = points[i].routeMi - points[j].routeMi;
            if (miles < 0.05 || miles > 20) continue;
            const grade = (points[i].elevation - points[j].elevation) / (miles * 5280) * 100;
            if (Math.abs(grade) >= minGradePct) windows.push({ start: j, end: i, grade });
        }

        const stretches = [];
        for (const w of windows) {
            const last = stretches[stretches.length - 1];
            if (last && Math.sign(last.grade) === Math.sign(w.grade) &&
                points[w.start].routeMi - points[last.end].routeMi < joinMi) {
                last.end = Math.max(last.end, w.end);
                if (Math.abs(w.grade) > Math.abs(last.grade)) last.grade = w.grade;
            } else {
                stretches.push({ ...w });
            }
        }
        return stretches.map(s => ({
            location: s.start,
            grade: Math.round(Math.abs(s.grade) * 10) / 10,
            isClimb: s.grade > 0,
            lengthMi: Math.round((points[s.end].routeMi - points[s.start].routeMi) * 100) / 100
        }));
    }

    /**
     * Get elevation from backend API (uses Google Maps API)
     */
    async getElevationFromBackend(sampledCoords, originalCoords) {
        const chunkSize = 100;
        const chunks = [];
        
        for (let i = 0; i < sampledCoords.length; i += chunkSize) {
            chunks.push(sampledCoords.slice(i, i + chunkSize));
        }
        
        const allResults = [];
        
        for (const chunk of chunks) {
            const locations = chunk.map(c => ({ lat: c.lat, lng: c.lng }));
            
            const response = await axios.post(`${this.backendUrl}/api/elevation`, {
                locations: locations
            });

            if (response.data.success && response.data.results) {
                allResults.push(...response.data.results);
            } else {
                throw new Error('Backend elevation API error');
            }
        }

        return this.processGoogleElevationData(allResults, originalCoords);
    }

    /**
     * Get elevation from Open-Elevation API (free fallback)
     */
    async getElevationFromOpenElevation(sampledCoords, originalCoords) {
        const chunkSize = 100;
        const chunks = [];
        
        for (let i = 0; i < sampledCoords.length; i += chunkSize) {
            chunks.push(sampledCoords.slice(i, i + chunkSize));
        }
        
        const allResults = [];
        
        for (const chunk of chunks) {
            const locations = chunk.map(c => ({
                latitude: c.lat,
                longitude: c.lng
            }));
            
            const response = await axios.post(this.openElevationUrl, {
                locations: locations
            });

            if (response.data && response.data.results) {
                allResults.push(...response.data.results);
            } else {
                throw new Error('Elevation API error: No results returned');
            }
        }

        return this.processOpenElevationData(allResults, originalCoords);
    }

    /**
     * Process elevation data from Google API (via backend)
     */
    processGoogleElevationData(results, originalCoords) {
        const totalDistance = this.calculateTotalDistance(originalCoords);
        
        const elevations = results.map((point, index) => ({
            distance: ((index / (results.length - 1)) * totalDistance).toFixed(2),
            elevation: Math.round(point.elevation * 3.28084), // Convert meters to feet
            lat: point.location.lat,
            lng: point.location.lng
        }));

        return {
            points: elevations,
            stats: this.calculateElevationStats(elevations)
        };
    }

    /**
     * Sample coordinates evenly along the route
     */
    sampleCoordinates(coordinates, samples) {
        if (coordinates.length <= samples) {
            return coordinates;
        }

        const sampled = [];
        const step = (coordinates.length - 1) / (samples - 1);

        for (let i = 0; i < samples; i++) {
            const index = Math.round(i * step);
            sampled.push(coordinates[index]);
        }

        return sampled;
    }

    /**
     * Process elevation data from Open-Elevation API response
     */
    processOpenElevationData(results, originalCoords) {
        const totalDistance = this.calculateTotalDistance(originalCoords);
        
        const elevations = results.map((point, index) => ({
            distance: ((index / (results.length - 1)) * totalDistance).toFixed(2),
            elevation: Math.round(point.elevation * 3.28084), // Convert meters to feet
            lat: point.latitude,
            lng: point.longitude
        }));

        return {
            points: elevations,
            stats: this.calculateElevationStats(elevations)
        };
    }

    /**
     * Smooth elevation data using moving average to remove spikes/noise
     * Use small window (3) to preserve real grade changes while removing noise
     */
    smoothElevationData(elevations, windowSize = 3) {
        if (elevations.length < windowSize) {
            return elevations;
        }

        const smoothed = [];
        const halfWindow = Math.floor(windowSize / 2);

        for (let i = 0; i < elevations.length; i++) {
            let sum = 0;
            let count = 0;

            // Average elevation over window
            for (let j = Math.max(0, i - halfWindow); j <= Math.min(elevations.length - 1, i + halfWindow); j++) {
                sum += elevations[j].elevation;
                count++;
            }

            smoothed.push({
                ...elevations[i],
                elevation: Math.round(sum / count)
            });
        }

        return smoothed;
    }

    /**
     * Calculate elevation statistics
     * Based on verified DOT data: I-70 max 7%, I-80 max 7%
     * Source: FHWA research and state DOT highway data
     */
    calculateElevationStats(elevations) {
        let maxElevation = -Infinity;
        let minElevation = Infinity;
        let steepGrades = [];
        let maxGrade = 0;

        for (let i = 0; i < elevations.length; i++) {
            maxElevation = Math.max(maxElevation, elevations[i].elevation);
            minElevation = Math.min(minElevation, elevations[i].elevation);
        }

        // Calculate grades between consecutive elevation points
        const grades = [];
        for (let i = 1; i < elevations.length; i++) {
            const elevDiff = elevations[i].elevation - elevations[i - 1].elevation;
            const distDiff = parseFloat(elevations[i].distance) - parseFloat(elevations[i - 1].distance);
            
            // Skip if distance is too small (near-duplicate points -- division
            // by a tiny distance amplifies GPS/elevation-API noise into wild
            // grade values) or too large (not a real local grade anymore).
            // This used to be a fixed 0.5mi floor, but getElevationAlongPath()
            // always requests 200-300 samples regardless of route length
            // (UIController.js) -- on a ~20mi local route that's ~0.1mi
            // between samples, under the old floor, so EVERY consecutive pair
            // got skipped and steepGrades came back empty no matter how steep
            // the real road was (confirmed on a real drive with a posted 9%
            // grade sign and zero grade waypoints reaching native all trip).
            // 0.05mi (~264ft) still filters near-duplicate noise without
            // discarding real short-route sampling density.
            if (distDiff < 0.05 || distDiff > 20) {
                continue;
            }
            
            // Calculate grade: (elevation change / horizontal distance) * 100
            const grade = Math.abs((elevDiff / (distDiff * 5280)) * 100);
            
            // Cap at 8% - verified max for I-70 and I-80 is 7%
            const cappedGrade = Math.min(grade, 8);
            
            grades.push({
                index: i,
                grade: cappedGrade,
                elevation: elevations[i].elevation,
                distance: elevations[i].distance,
                distSegment: distDiff,
                isClimb: elevDiff > 0
            });
            maxGrade = Math.max(maxGrade, cappedGrade);
        }

        // Filter steep grades: an absolute 4% threshold alone -- the previous
        // version also required being 2%+ above the route's OWN average,
        // which backfires on a route that's consistently mountainous
        // throughout (like I-70 through the Rockies): the average grade is
        // already elevated, so a real steep stretch isn't "above average"
        // and got filtered out -- exactly backwards, since a uniformly
        // mountainous route is the one that most needs grade warnings, not
        // the one that should suppress them.
        const avgGrade = grades.length > 0
            ? grades.reduce((sum, g) => sum + g.grade, 0) / grades.length
            : 0;

        steepGrades = grades.filter(g => g.grade > 4).map(g => ({
            location: g.index,
            grade: Math.round(g.grade * 10) / 10,
            elevation: g.elevation,
            distance: g.distance,
            isClimb: g.isClimb
        }));

        console.log(`Elevation stats: ${steepGrades.length} steep grades detected, max grade: ${Math.round(maxGrade * 10) / 10}%, avg grade: ${Math.round(avgGrade * 10) / 10}%`);
        if (steepGrades.length > 0) {
            console.log('Steep grades:', steepGrades.slice(0, 5).map(g => `${g.grade}% at ${g.distance}mi`));
        }

        return {
            maxElevation: Math.round(maxElevation),
            minElevation: Math.round(minElevation),
            elevationChange: Math.round(maxElevation - minElevation),
            maxGrade: Math.round(maxGrade * 10) / 10,
            steepGrades: steepGrades,
            steepGradeCount: steepGrades.length
        };
    }

    /**
     * Generate mock elevation data for demo
     */
    generateMockElevationData(coordinates, samples) {
        const elevations = [];
        const totalDistance = this.calculateTotalDistance(coordinates);

        // Detect if this is a mountainous route based on coordinate changes
        const latRange = Math.max(...coordinates.map(c => c.lat)) - Math.min(...coordinates.map(c => c.lat));
        const lngRange = Math.max(...coordinates.map(c => c.lng)) - Math.min(...coordinates.map(c => c.lng));
        
        // Check if route goes through Rocky Mountains (rough approximation)
        const isRockyMountains = coordinates.some(c => 
            c.lat > 37 && c.lat < 42 && c.lng > -111 && c.lng < -105
        );
        
        // Check if route goes through Appalachians
        const isAppalachians = coordinates.some(c =>
            c.lat > 35 && c.lat < 42 && c.lng > -84 && c.lng < -75
        );

        for (let i = 0; i < samples; i++) {
            const progress = i / (samples - 1);
            const coordIndex = Math.floor(progress * (coordinates.length - 1));
            const coord = coordinates[coordIndex];

            let elevation;
            
            if (isRockyMountains) {
                // Simulate Rocky Mountain elevation profile (Denver to Utah I-70)
                // Denver starts ~5280ft, climbs to ~11,000ft at Eisenhower Tunnel, drops to ~4500ft in Utah
                const baseElevation = 5280;
                
                if (progress < 0.15) {
                    // Climbing from Denver
                    elevation = baseElevation + (progress / 0.15) * 5500 + Math.random() * 300;
                } else if (progress < 0.25) {
                    // Steep climb to Eisenhower Tunnel
                    const localProgress = (progress - 0.15) / 0.10;
                    elevation = 10780 + localProgress * 400 + Math.random() * 200;
                } else if (progress < 0.35) {
                    // Descent from tunnel
                    const localProgress = (progress - 0.25) / 0.10;
                    elevation = 11180 - localProgress * 2000 + Math.random() * 300;
                } else if (progress < 0.60) {
                    // Rolling through mountains
                    elevation = 7000 + Math.sin(progress * Math.PI * 8) * 1500 + Math.random() * 400;
                } else {
                    // Descent to Utah
                    const localProgress = (progress - 0.60) / 0.40;
                    elevation = 8000 - localProgress * 3500 + Math.sin(localProgress * Math.PI * 4) * 800 + Math.random() * 300;
                }
            } else if (isAppalachians) {
                // Simulate Appalachian elevation profile
                const baseElevation = 1000;
                elevation = baseElevation + Math.sin(progress * Math.PI * 6) * 2000 + Math.random() * 200;
            } else {
                // Flatter terrain with gentle rolling hills
                const baseElevation = 500 + Math.random() * 500;
                elevation = baseElevation + Math.sin(progress * Math.PI * 3) * 300 + Math.random() * 100;
            }

            elevations.push({
                distance: (progress * totalDistance).toFixed(2),
                elevation: Math.round(elevation),
                lat: coord.lat,
                lng: coord.lng
            });
        }

        return {
            points: elevations,
            stats: this.calculateElevationStats(elevations)
        };
    }

    /**
     * Calculate total distance between coordinates (simplified)
     */
    calculateTotalDistance(coordinates) {
        let total = 0;
        for (let i = 1; i < coordinates.length; i++) {
            total += this.haversineDistance(
                coordinates[i - 1].lat,
                coordinates[i - 1].lng,
                coordinates[i].lat,
                coordinates[i].lng
            );
        }
        return total;
    }

    /**
     * Calculate distance between two points using Haversine formula
     */
    haversineDistance(lat1, lon1, lat2, lon2) {
        const R = 3959; // Earth's radius in miles
        const dLat = this.toRad(lat2 - lat1);
        const dLon = this.toRad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    toRad(degrees) {
        return degrees * (Math.PI / 180);
    }
}
